import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ExternalLink, CreditCard, Smartphone, HelpCircle, Lock } from "lucide-react";
import useSeo from "../hooks/useSeo";
import useReveal from "../hooks/useReveal";
import api, { API } from "../lib/api";
import SiteSeal from "../components/SiteSeal";
import { business } from "../data/siteData";

export default function Payment() {
  useSeo(
    "Make a Payment: Connecticut: Mr. Resurface",
    "Pay your Mr. Resurface deposit, partial payment or remaining balance through our secure hosted checkout. Credit cards, Apple Pay and Google Pay accepted."
  );
  useReveal();
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  useEffect(() => {
    api
      .get("/site/config")
      .then(({ data }) => setCheckoutUrl(data.payment_url || null))
      .catch(() => setCheckoutUrl(null));
  }, []);

  return (
    <>
      <section className="border-b-4 border-[#244aa2] bg-[#f7f7f7] py-12">
        <div className="mr-container">
          <h1 className="mr-h1 text-3xl sm:text-4xl">Make a Payment</h1>
          <p className="mt-3 max-w-2xl text-[#575757]">
            Pay a deposit, a partial payment, your remaining balance or the full amount for your
            Mr. Resurface project.
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mr-container-wide grid gap-10 lg:grid-cols-3">
          <div className="mr-body reveal lg:col-span-2">
            <div className="reveal">
              {checkoutUrl ? (
                <a
                  href={checkoutUrl}
                  data-testid="payment-checkout-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-[#244aa2] px-9 py-5 text-[14px] font-bold uppercase tracking-[0.1em] text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#0c2a66]"
                >
                  <Lock className="h-5 w-5" />
                  Proceed to Secure Payment
                  <ExternalLink className="h-4 w-4" />
                </a>
              ) : (
                <div className="inline-flex items-center gap-3 bg-neutral-200 px-9 py-5 text-[14px] font-bold uppercase tracking-[0.1em] text-neutral-500">
                  Loading secure checkout…
                </div>
              )}
              <p className="mt-3 text-[13px] text-neutral-500">
                Opens the hosted checkout in a new secure tab.
              </p>
              <div className="mt-6 flex items-center gap-4" data-testid="payment-seal-row">
                <SiteSeal />
                <p className="mb-0 max-w-[260px] text-[12px] leading-relaxed text-neutral-500">
                  CompliAssure&trade; Secured &mdash; this site is scanned for PCI compliance.
                  Click the seal to view the verification.
                </p>
              </div>
            </div>

            <div className="reveal mt-10 grid gap-5 sm:grid-cols-3">
              {[
                { icon: CreditCard, title: "Credit & debit cards", copy: "Visa, Mastercard, American Express and Discover." },
                { icon: Smartphone, title: "Apple Pay & Google Pay", copy: "Pay in a couple of taps from your phone." },
                { icon: ShieldCheck, title: "Secure hosted checkout", copy: "Card details are handled entirely by the payment processor." },
              ].map(({ icon: Icon, title, copy }) => (
                <div key={title} className="border border-neutral-200 p-5">
                  <Icon className="h-6 w-6 text-[#a10000]" />
                  <h3 className="font-head mt-3 text-[15px] font-extrabold text-[#242424]">{title}</h3>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-[#575757]">{copy}</p>
                </div>
              ))}
            </div>

            <div className="reveal mt-10 flex items-start gap-3 border-l-4 border-[#244aa2] bg-[#f7f7f7] p-6">
              <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-[#244aa2]" />
              <p className="mb-0 text-[15px] leading-relaxed text-[#575757]">
                <strong>MrResurface.com never collects, processes, transmits or stores your card
                information.</strong>{" "}
                Payment is completed entirely on the payment provider&rsquo;s own PCI-compliant
                hosted checkout page.
              </p>
            </div>

            <div className="reveal mt-8 flex items-start gap-3">
              <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#a10000]" />
              <p className="mb-0 text-[15px] text-[#575757]">
                Questions about an invoice or a receipt? Call{" "}
                <a href={`tel:${business.phoneRaw}`}>{business.phone}</a> or email{" "}
                <a href={`mailto:${business.email}`}>{business.email}</a>. See also our{" "}
                <Link to="/billing">billing terms</Link>.
              </p>
            </div>
          </div>

          <aside className="reveal space-y-6">
            <div className="border border-neutral-200 bg-white p-7 text-center shadow-sm">
              <h3 className="mr-h3 text-lg">Scan to pay</h3>
              <p className="mt-2 text-[14px] text-[#575757]">
                Point your phone camera at this code to open the same secure checkout.
              </p>
              <img
                src={`${API}/qr/payment.png?size=9`}
                data-testid="payment-qr-image"
                alt="QR code that opens the Mr. Resurface secure payment checkout"
                className="mx-auto mt-5 w-[220px] max-w-full border border-neutral-200"
              />
              <p className="mt-4 text-[12px] text-neutral-500">
                The same code is downloadable from the private owner dashboard for invoices,
                estimates, business cards, handouts and signage.
              </p>
            </div>

            <div className="border border-neutral-200 bg-[#f7f7f7] p-7 text-center shadow-sm">
              <h3 className="mr-h3 text-lg">Happy with the work?</h3>
              <p className="mt-2 text-[14px] text-[#575757]">
                Scan to leave Mr.&nbsp;Resurface a Google review — it genuinely helps a small
                family business.
              </p>
              <img
                src={`${API}/qr/google-review.png?size=9`}
                data-testid="google-review-qr-image"
                alt="QR code that opens the Mr. Resurface Google review page"
                className="mx-auto mt-5 w-[180px] max-w-full border border-neutral-200 bg-white"
              />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
